/**
 * Design System §2 — the single mapping from domain status to visual tone.
 *
 * The `satisfies Record<Union, Tone>` is the point: a new verdict or instruction from the API
 * fails `tsc` until someone decides what it means visually, instead of silently rendering neutral.
 */

import type {
  EditorialImportance,
  EditorialPublicationInstruction,
  EditorialSentimentValue,
  EditorialStancePosition,
  EditorialVerdict,
  SocialClassification,
} from 'src/types/editorial';

export const TONES = ['supported', 'partial', 'contradicted', 'unresolved', 'info', 'social'] as const;
export type Tone = (typeof TONES)[number];

export const VERDICT_TONE = {
  supported: 'supported',
  partially_supported: 'partial',
  contradicted: 'contradicted',
  unresolved: 'unresolved',
  not_verifiable: 'unresolved',
  not_required: 'unresolved',
} satisfies Record<EditorialVerdict, Tone>;

export const VERDICT_LABEL = {
  supported: 'Sustentado',
  partially_supported: 'Parcialmente sustentado',
  contradicted: 'Contradicho',
  unresolved: 'Sin resolver',
  not_verifiable: 'No verificable',
  not_required: 'No requiere verificación',
} satisfies Record<EditorialVerdict, string>;

export const PUBLICATION_TONE = {
  state_as_fact_with_citation: 'supported',
  state_with_reduced_scope: 'partial',
  attribute_to_speaker: 'unresolved',
  report_as_dispute: 'partial',
  do_not_publish: 'contradicted',
} satisfies Record<EditorialPublicationInstruction, Tone>;

export const PUBLICATION_LABEL = {
  state_as_fact_with_citation: 'Publicable como hecho, con cita',
  state_with_reduced_scope: 'Publicable con alcance reducido',
  attribute_to_speaker: 'Atribuir al hablante',
  report_as_dispute: 'Reportar como disputa',
  do_not_publish: 'No publicar',
} satisfies Record<EditorialPublicationInstruction, string>;

/** Art Direction §8: social relevance never borrows a verification tone, whatever the score. */
export const SOCIAL_TONE: Tone = 'social';

export const SOCIAL_CLASSIFICATION_LABEL = {
  irrelevant: 'Irrelevante',
  emerging: 'Emergente',
  relevant: 'Relevante',
  high: 'Alta',
  breaking_candidate: 'Posible breaking',
} satisfies Record<SocialClassification, string>;

/**
 * Which step of the violet ramp the meter fills with. Not a hue change — one family, five
 * intensities, so a wall of reviews sorts itself visually before a single number is read. The
 * classification is the documented band Art Direction §8 requires before tinting anything.
 */
export const SOCIAL_CLASSIFICATION_STEP = {
  irrelevant: 1,
  emerging: 2,
  relevant: 3,
  high: 4,
  breaking_candidate: 5,
} satisfies Record<SocialClassification, 1 | 2 | 3 | 4 | 5>;

export const REVIEW_ACTION_TONE = {
  approved: 'supported',
  edited: 'partial',
  rejected: 'contradicted',
  published: 'supported',
} satisfies Record<'approved' | 'edited' | 'rejected' | 'published', Tone>;

/** Draft statuses the pipeline can produce for an editorial output. */
export const OUTPUT_STATUS_TONE: Record<string, Tone> = {
  pending_human_review: 'partial',
  validation_failed: 'contradicted',
  blocked_eligibility: 'unresolved',
  generation_failed: 'contradicted',
};

export function outputStatusTone(status: string): Tone {
  return OUTPUT_STATUS_TONE[status] ?? 'unresolved';
}

/** Verification statuses. `blocked_*` is a finding, not an empty state (Composition §7). */
export const VERIFICATION_STATUS_TONE: Record<string, Tone> = {
  verified: 'supported',
  contradiction_lead: 'partial',
  blocked_critical_claim: 'contradicted',
  blocked_no_sources: 'unresolved',
  blocked_input: 'unresolved',
  research_failed: 'contradicted',
};

export function verificationStatusTone(status: string): Tone {
  return VERIFICATION_STATUS_TONE[status] ?? 'unresolved';
}

/** Editorial importance is a signal-extraction judgment, not a verdict — it never borrows
 *  `supported`/`contradicted`, which this product reserves for verification (Art Direction §8). */
export const EDITORIAL_IMPORTANCE_TONE = {
  low: 'unresolved',
  medium: 'info',
  high: 'partial',
} satisfies Record<EditorialImportance, Tone>;

export const EDITORIAL_IMPORTANCE_LABEL = {
  low: 'importancia baja',
  medium: 'importancia media',
  high: 'importancia alta',
} satisfies Record<EditorialImportance, string>;

/** Sentiment and stance describe tone of coverage, not truth — plain labels, no tone mapping,
 *  so neither is ever mistaken for a verification color. */
export const SENTIMENT_LABEL = {
  positive: 'positivo',
  neutral: 'neutro',
  negative: 'negativo',
} satisfies Record<EditorialSentimentValue, string>;

export const STANCE_LABEL = {
  supportive: 'a favor',
  neutral: 'neutral',
  critical: 'crítica',
} satisfies Record<EditorialStancePosition, string>;
